import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { FilterCriteria } from '../../../../models/api/reports/filter-detail-response';
import { BaseComponent } from '../../../base/base.component';
import { CheckboxComponent } from '../../../core/controls/checkbox.component';
import { RangeComponent } from '../../../core/controls/range.component';
import { FilterSection, ICriteria } from '../edit-filter.component';

@Component({
  selector: 'app-work-status-criteria',
  imports: [CommonModule, CheckboxComponent, RangeComponent],
  template: `
    <div class="tw-pt-2">
      <div class="tw-flex tw-flex-col tw-gap-0 tw-w-full">
        <div
          class="tw-w-full tw-italic tw-pb-4 tw-mb-2 tw-text-gray-600 tw-max-w-[800px]"
        >
          Select a work status to return claims where the selected status is
          effective on the As of date in the report. Adding an effective date
          range will expand the results to include claims where the selected
          status is effective at any point within that timeframe. If multiple
          work statuses are selected, results will include any of them (e.g.,
          Off Work <strong class="tw-not-italic">or</strong> Restricted).
        </div>
        <ng-template #select let-item>
          <div class="tw-flex tw-items-center tw-pb-2">
            <div class="tw-basis-[200px] tw-pb-[32px]">
              <app-checkbox
                [label]="item.label"
                [control]="item.enabled"
                [showHelpText]="false"
              />
            </div>
            <div>
              <app-range
                [control]="item.from"
                [toControl]="item.to"
                [showHelpText]="true"
                [screenReaderLabel]="item.label + 'effective date'"
                [maxSpanDays]="null"
              />
            </div>
          </div>
        </ng-template>

        <div class="tw-flex tw-items-center tw-pb-2">
          <div class="tw-basis-[200px] tw-font-bold">Work status</div>
          <div class="tw-font-bold">Effective date</div>
        </div>

        <ng-container
          *ngTemplateOutlet="
            select;
            context: {
              $implicit: {
                label: 'Normal',
                enabled: fg.controls.normalEnabled,
                from: fg.controls.normalFrom,
                to: fg.controls.normalTo,
              },
            }
          "
        ></ng-container>

        <ng-container
          *ngTemplateOutlet="
            select;
            context: {
              $implicit: {
                label: 'Off work',
                enabled: fg.controls.offWorkEnabled,
                from: fg.controls.offWorkFrom,
                to: fg.controls.offWorkTo,
              },
            }
          "
        ></ng-container>

        <ng-container
          *ngTemplateOutlet="
            select;
            context: {
              $implicit: {
                label: 'Restricted',
                enabled: fg.controls.restrictedEnabled,
                from: fg.controls.restrictedFrom,
                to: fg.controls.restrictedTo,
              },
            }
          "
        ></ng-container>

        <ng-container
          *ngTemplateOutlet="
            select;
            context: {
              $implicit: {
                label: 'Restricted-NTL',
                enabled: fg.controls.restrictedNtlEnabled,
                from: fg.controls.restrictedNtlFrom,
                to: fg.controls.restrictedNtlTo,
              },
            }
          "
        ></ng-container>

        <ng-container
          *ngTemplateOutlet="
            select;
            context: {
              $implicit: {
                label: 'Terminated',
                enabled: fg.controls.terminatedEnabled,
                from: fg.controls.terminatedFrom,
                to: fg.controls.terminatedTo,
              },
            }
          "
        ></ng-container>
      </div>
    </div>
  `,
  styles: ``,
})
export class WorkStatusCriteriaComponent
  extends BaseComponent
  implements OnInit, ICriteria
{
  fg = new FormGroup({
    normalEnabled: new FormControl<boolean>(false),
    normalFrom: new FormControl<Date>({ value: null, disabled: true }),
    normalTo: new FormControl<Date>({ value: null, disabled: true }),

    offWorkEnabled: new FormControl<boolean>(false),
    offWorkFrom: new FormControl<Date>({ value: null, disabled: true }),
    offWorkTo: new FormControl<Date>({ value: null, disabled: true }),

    restrictedEnabled: new FormControl<boolean>(false),
    restrictedFrom: new FormControl<Date>({ value: null, disabled: true }),
    restrictedTo: new FormControl<Date>({ value: null, disabled: true }),

    restrictedNtlEnabled: new FormControl<boolean>(false),
    restrictedNtlFrom: new FormControl<Date>({ value: null, disabled: true }),
    restrictedNtlTo: new FormControl<Date>({ value: null, disabled: true }),

    terminatedEnabled: new FormControl<boolean>(false),
    terminatedFrom: new FormControl<Date>({ value: null, disabled: true }),
    terminatedTo: new FormControl<Date>({ value: null, disabled: true }),
  });

  @Input({ required: true })
  section: FilterSection;

  workStatuses: WorkStatus[] = [
    {
      label: 'Normal',
      system: 'juris',
      table: 'work_status_as_of_4',
      field: 'DATE_NORMAL_WORK_STATUS',
      enabled: this.fg.controls.normalEnabled,
      from: this.fg.controls.normalFrom,
      to: this.fg.controls.normalTo,
    },
    {
      label: 'Off work',
      system: 'juris',
      table: 'work_status_as_of_4',
      field: 'DATE_OFF_WORK_WORK_STATUS',
      enabled: this.fg.controls.offWorkEnabled,
      from: this.fg.controls.offWorkFrom,
      to: this.fg.controls.offWorkTo,
    },
    {
      label: 'Restricted',
      system: 'juris',
      table: 'work_status_as_of_4',
      field: 'DATE_RESTRICTED_WORK_STATUS',
      enabled: this.fg.controls.restrictedEnabled,
      from: this.fg.controls.restrictedFrom,
      to: this.fg.controls.restrictedTo,
    },
    {
      label: 'Restricted-NTL',
      system: 'juris',
      table: 'work_status_as_of_4',
      field: 'DATE_RESTRICTED_NTL_WORK_STATUS',
      enabled: this.fg.controls.restrictedNtlEnabled,
      from: this.fg.controls.restrictedNtlFrom,
      to: this.fg.controls.restrictedNtlTo,
    },
    {
      label: 'Terminated',
      system: 'juris',
      table: 'work_status_as_of_4',
      field: 'DATE_TERMINATED_WORK_STATUS',
      enabled: this.fg.controls.terminatedEnabled,
      from: this.fg.controls.terminatedFrom,
      to: this.fg.controls.terminatedTo,
    },
  ];

  setWorkStatusFromCriteria(criteria: FilterCriteria[]) {
    this.workStatuses.forEach((workStatus) => {
      const found = criteria.find(
        (c) =>
          (c.tableAlias === workStatus.table &&
            c.column === workStatus.field) ||
          c.values?.some(
            (v) => v.toLowerCase() === workStatus.label.toLowerCase(),
          ),
      );

      if (!found) return;

      const value = found.values?.[0] || '';

      if (found.tableAlias === 'work_status_as_of_3') {
        workStatus.enabled.setValue(true);
      } else if (found.tableAlias === 'work_status_as_of_4') {
        workStatus.enabled.setValue(true);
        const [from, to] = value.split('|');
        workStatus.from.setValue(this.parseDate(from));
        workStatus.to.setValue(this.parseDate(to));
      }
    });
  }

  private parseDate(dateStr: string): Date | null {
    if (!dateStr) return null;
    const parts = dateStr.split('-');
    return new Date(Number(parts[2]), Number(parts[0]) - 1, Number(parts[1]));
  }

  async ngOnInit() {
    this.section.criteria = this;
    this.dispose.subs = this.fg.controls.normalEnabled.valueChanges.subscribe(
      (enabled) =>
        this.toggleControls(
          enabled,
          this.fg.controls.normalFrom,
          this.fg.controls.normalTo,
        ),
    );

    this.dispose.subs = this.fg.controls.offWorkEnabled.valueChanges.subscribe(
      (enabled) =>
        this.toggleControls(
          enabled,
          this.fg.controls.offWorkFrom,
          this.fg.controls.offWorkTo,
        ),
    );

    this.dispose.subs =
      this.fg.controls.restrictedEnabled.valueChanges.subscribe((enabled) =>
        this.toggleControls(
          enabled,
          this.fg.controls.restrictedFrom,
          this.fg.controls.restrictedTo,
        ),
      );

    this.dispose.subs =
      this.fg.controls.restrictedNtlEnabled.valueChanges.subscribe((enabled) =>
        this.toggleControls(
          enabled,
          this.fg.controls.restrictedNtlFrom,
          this.fg.controls.restrictedNtlTo,
        ),
      );

    this.dispose.subs =
      this.fg.controls.terminatedEnabled.valueChanges.subscribe((enabled) => {
        this.toggleControls(
          enabled,
          this.fg.controls.terminatedFrom,
          this.fg.controls.terminatedTo,
        );
      });

    this.workStatuses.forEach((workStatus) => {
      this.dispose.subs = workStatus.enabled.valueChanges.subscribe(() => {
        this.emitCount();
      });
    });

    this.setupDateValidation(
      this.fg.controls.normalFrom,
      this.fg.controls.normalTo,
    );
    this.setupDateValidation(
      this.fg.controls.offWorkFrom,
      this.fg.controls.offWorkTo,
    );
    this.setupDateValidation(
      this.fg.controls.restrictedFrom,
      this.fg.controls.restrictedTo,
    );
    this.setupDateValidation(
      this.fg.controls.restrictedNtlFrom,
      this.fg.controls.restrictedNtlTo,
    );
    this.setupDateValidation(
      this.fg.controls.terminatedFrom,
      this.fg.controls.terminatedTo,
    );

    if (this.section.values?.length) {
      this.setWorkStatusFromCriteria(this.section.values);
    }
  }

  getCriteria(): FilterCriteria[] {
    return this.workStatuses
      .filter((workStatus) => workStatus.enabled.value)
      .map((workStatus) => {
        let tableAlias = workStatus.table;
        let column = workStatus.field;
        let operator = 'BETWEEN';
        let values: string[] = [];

        const hasBothDates = workStatus.from.value && workStatus.to.value;

        if (!hasBothDates) {
          tableAlias = 'work_status_as_of_3';
          column = 'WORK_STATUS';
          operator = 'EQUALS';
          switch (workStatus.label) {
            case 'Normal':
              values = ['NORMAL'];
              break;
            case 'Off work':
              values = ['OFF WORK'];
              break;
            case 'Restricted':
              values = ['RESTRICTED'];
              break;
            case 'Restricted-NTL':
              values = ['RESTRICTED-NTL'];
              break;
            case 'Terminated':
              values = ['TERMINATED'];
              break;
          }
        } else {
          values = [
            this.formatDateRange(workStatus.from.value, workStatus.to.value),
          ];
        }

        return {
          tableAlias: tableAlias,
          column: column,
          displayName: workStatus.label,
          operator: operator,
          values: values,
          lineOfBusiness: '',
          dataType: '',
          isClaimLab: false,
          formattedOperator: '',
          formattedValue: '',
          filterSection: 'Work Status',
          filterSubsection: '',
        };
      });
  }

  private toggleControls(
    enabled: boolean,
    from: FormControl,
    to: FormControl,
  ): void {
    if (enabled) {
      from.enable();
      to.enable();
    } else {
      from.disable();
      to.disable();
      from.setValue(null);
      to.setValue(null);
      from.removeValidators(Validators.required);
      to.removeValidators(Validators.required);
      from.markAsUntouched();
      from.markAsPristine();
      to.markAsUntouched();
      to.markAsPristine();
    }
  }

  private formatDateRange(from: Date, to: Date): string {
    const formatDate = (date: Date): string => {
      return `${date.getMonth() + 1}-${date.getDate()}-${date.getFullYear()}`;
    };

    return `${formatDate(from)}|${formatDate(to)}`;
  }

  private setupDateValidation(
    from: FormControl<Date>,
    to: FormControl<Date>,
  ): void {
    this.dispose.subs = from.valueChanges.subscribe((value) => {
      if (value) {
        to.addValidators(Validators.required);
        to.markAsTouched();
      } else if (!to.value) {
        to.removeValidators(Validators.required);
      }
      to.updateValueAndValidity({ emitEvent: false });
    });

    this.dispose.subs = to.valueChanges.subscribe((value) => {
      if (value) {
        from.addValidators(Validators.required);
        from.markAsTouched();
      } else if (!from.value) {
        from.removeValidators(Validators.required);
      }
      from.updateValueAndValidity({ emitEvent: false });
    });
  }

  valid(): boolean {
    return this.fg.valid;
  }

  private emitCount() {
    const count = this.workStatuses.filter(
      (workStatus) => workStatus.enabled.value,
    ).length;
    this.section.count.set(count);
  }
}

interface WorkStatus {
  label: string;
  system: string;
  table: string;
  field: string;
  enabled: FormControl<boolean>;
  from: FormControl<Date>;
  to: FormControl<Date>;
}
